'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { Expand } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Lightbox } from './lightbox'
import { useLightbox } from './lightbox-provider'
import './lightbox.css'

export interface ZoomableImageProps {
  /** Image source — also the source shown enlarged in the overlay. */
  src: string
  /** Alt text — drives the thumbnail, the overlay image, and the dialog label. */
  alt: string
  /** Optional caption rendered under the thumbnail (mono / muted). */
  caption?: string
  /** White surface + hairline border + soft shadow frame (landing usage). */
  framed?: boolean
  /** Bottom-right badge text; defaults to the localized "tap to zoom". */
  zoomHint?: string
  /** Extra class on the root <figure> (e.g. reveal). */
  className?: string
  /** Inline style on the root <figure> (e.g. reveal delay). */
  style?: React.CSSProperties
  /** Forwarded to the root <figure> (e.g. the reveal observer ref). */
  ref?: React.Ref<HTMLElement>
  /** Custom trigger content; when omitted a default <img> thumbnail renders. */
  children?: React.ReactNode
  /** Intrinsic size for the default thumbnail (prevents layout shift). */
  width?: number
  height?: number
}

/**
 * A clickable image that opens the shared zoom/pan {@link Lightbox}.
 *
 * Reuse anywhere — screenshots, product shots, avatars, galleries. When a
 * {@link LightboxProvider} is mounted it drives the single app-wide overlay;
 * rendered standalone (no provider) it falls back to a self-contained local
 * overlay so it still works in Storybook or an isolated mount.
 */
export function ZoomableImage({
  src,
  alt,
  caption,
  framed = false,
  zoomHint,
  className,
  style,
  ref,
  children,
  width,
  height,
}: ZoomableImageProps) {
  const { t } = useTranslation(['web'])
  const lightbox = useLightbox()
  const [localItem, setLocalItem] = useState<{ src: string; alt: string } | null>(null)

  const activate = () => {
    if (lightbox) lightbox.open({ src, alt })
    else setLocalItem({ src, alt })
  }

  const hint = zoomHint ?? t('web:zoomImage.zoomHint')

  return (
    <figure
      ref={ref}
      className={['kiao-zoom', framed ? 'kiao-zoom-framed' : '', className].filter(Boolean).join(' ')}
      style={style}
    >
      <button
        type="button"
        className="kiao-zoom-trigger"
        aria-label={alt}
        aria-haspopup="dialog"
        onClick={activate}
      >
        {children ??
          // Optimize local static thumbnails through next/image (AVIF/webp +
          // responsive srcset, lazy by default). The `.kiao-zoom-thumb` class
          // keeps it fluid (width:100%; height:auto), so RWD is unchanged; the
          // intrinsic width/height drive the aspect ratio (no CLS). Remote/inline
          // srcs (incl. protocol-relative `//host/…`) or callers that omit
          // dimensions fall back to a plain <img> so the shared component never
          // breaks next/image's contract.
          (width && height && src.startsWith('/') && !src.startsWith('//') ? (
            <Image
              src={src}
              alt={alt}
              width={width}
              height={height}
              sizes="(max-width: 1180px) 100vw, 1140px"
              className="kiao-zoom-thumb"
            />
          ) : (
            <img
              src={src}
              alt={alt}
              width={width}
              height={height}
              loading="lazy"
              className="kiao-zoom-thumb"
            />
          ))}
        <span className="kiao-zoom-badge" aria-hidden="true">
          <Expand size={13} strokeWidth={1.8} aria-hidden="true" />
          {hint}
        </span>
      </button>
      {caption ? <figcaption className="kiao-zoom-cap">{caption}</figcaption> : null}

      {/* Standalone fallback overlay — only used when no provider is present. */}
      {!lightbox ? <Lightbox item={localItem} onClose={() => setLocalItem(null)} /> : null}
    </figure>
  )
}
